import { ArrowChevronDownIcon } from '@tableau/icons/ArrowChevronDownIcon';
import { ArrowChevronUpIcon } from '@tableau/icons/ArrowChevronUpIcon';
import { EmbedBaseIcon } from '@tableau/icons/EmbedBaseIcon';
import { ButtonType, ButtonWidget, TextAreaWidget } from '@tableau/widgets';
import React, { useState } from 'react';

import { Messages } from '~src/codegen/Localize';
import { PageHeaderStyles } from '~src/spacesPlatform/components/layout/PageHeaderStyles';

import * as styles from '../styles/EmbedCodeStyle';
import { copyToClipboard } from './PublicFacingUrlCopyButton';

const EMBED_CODE_TEXT_FIELD_TEST_ID = 'embedcode-description';
const EMBED_CODE_COPY_BUTTON_TEST_ID = 'embedcode-copy-button';

interface EmbedCodeProps {
	publicFacingUrl: string;
	contentName?: string;
}

export const EmbedCodeCopyButton = (props: EmbedCodeProps): JSX.Element => {
	return <ButtonWidget
		buttonType={ButtonType.Primary}
		style={styles.button}
		testId={EMBED_CODE_COPY_BUTTON_TEST_ID}
		getContent={buttonContent}
		onClick={() => handleClickCopyLink(props)}
	/>;
};

export const EmbedCodeDropdown = (props: EmbedCodeProps): JSX.Element => {
	const [isExpanded, setExpanded] = useState(false);

	return (
		<div style={styles.dropdownContainer}>
			<ButtonWidget
				buttonType={ButtonType.IconOutline}
				style={styles.dropdown}
				getContent={() => dropdownContent(isExpanded)}
				onClick={() => setExpanded(!isExpanded)}
			/>
			{isExpanded ?
				<EmbedCodeTextField publicFacingUrl={props.publicFacingUrl} /> : null}
		</div>
	);
};

export const EmbedCodeTextField = (props: EmbedCodeProps): JSX.Element => {
	const EmbedCode = buildBroadcastEmbedCode(props.publicFacingUrl);
	return <TextAreaWidget
		value={EmbedCode}
		style={styles.textField}
		label=''
		testId={EMBED_CODE_TEXT_FIELD_TEST_ID}
		readOnly
		autoHeight
	/>;
};

const dropdownContent = (isExpanded: boolean): JSX.Element => {
	const dropdownMessage = isExpanded ? Messages.broadcastHideEmbedCodeDropdown() : Messages.broadcastShowEmbedCodeDropdown();
	return (
		<span
			className={PageHeaderStyles.TextButton}
		>
			{dropdownMessage}
			{isExpanded
				? <ArrowChevronUpIcon size={14} alt='' style={styles.chevron} />
				: <ArrowChevronDownIcon size={14} alt='' style={styles.chevron} />}
		</span>
	);
};


const buttonContent = (): JSX.Element => {
	return (
		<span className={PageHeaderStyles.TextButton}>
			<EmbedBaseIcon size={14} alt='' style={PageHeaderStyles.iconForTextButton} />
			{Messages.broadcastCopyEmbedCode()}
		</span>
	);
};

const handleClickCopyLink = (props: EmbedCodeProps): void => {
	const EmbedCode = buildBroadcastEmbedCode(props.publicFacingUrl);
	const successMessage = Messages.broadcastEmbedCodeCopiedNotificationText({ CONTENT_NAME: props.contentName });
	copyToClipboard(EmbedCode, successMessage);
};

const buildBroadcastEmbedCode = (broadcastPublicUrl: string): string => {
	const cleanedPublicURI = broadcastPublicUrl && broadcastPublicUrl.match(/.*(?=\?)/); //Extract URI, removing the query params
	const embedV3Library = `https://broadcast.tableau.com/javascripts/api/tableau.embedding.3.latest.min.js`;
	const scriptTag = `<script type='module' src='${embedV3Library}'></script>`;
	const vizTag = `<tableau-viz id='tableau-viz' src='${cleanedPublicURI}' hide-tabs>\n      <custom-parameter name=":showVizHome" value="n"></custom-parameter>\n      </tableau-viz>`;
	const embedCode = `<html>\n   <head> \n      ${scriptTag}\n   </head> \n   <body>\n      ${vizTag}\n   </body>\n</html>`;
	return embedCode;
};
